import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import toast from "react-hot-toast";
import claimsGQL from "../../gql/claims";
import "./styles/pre-auth.css";
import "../Beneficiary/styles/BeneficiaryList.css";
import AppModal from "../../components/AppModal";
import { Divider, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ClaimHistoryFilter from "./ClaimHistoryFilter";

import AppButton from "../../shared/AppButton";

export const convertToISOFormat = (dateString) => {
  const parts = dateString.split("/");
  const month = parts[0].padStart(2, "0");
  const day = parts[1].padStart(2, "0");
  const year = parts[2];

  return `${year}-${month}-${day}`;
};

function ClaimHistoryList(props) {
  const { t, i18n } = useTranslation();
  const currentDate = new Date();
  const currentYearMonth = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth(),
  ).padStart(2, "0")}`;

  function getFirstAndLastDateOfMonth(yearMonth) {
    const [year, month] = yearMonth.split("-").map(Number);
    const firstDate = new Date(year, month - 1, 1);
    const lastDate = new Date(year, month, 0);

    const firstDateString = `${firstDate.getFullYear()}-${(
      firstDate.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${firstDate.getDate().toString().padStart(2, "0")}`;
    const lastDateString = `${lastDate.getFullYear()}-${(
      lastDate.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${lastDate.getDate().toString().padStart(2, "0")}`;

    return {
      firstDate: firstDateString,
      lastDate: lastDateString,
    };
  }

  const initialDates = getFirstAndLastDateOfMonth(currentYearMonth);
  const [showFilter, setShowFilter] = useState(false);
  const [fromDate, setFromDate] = useState(initialDates.firstDate);
  const [toDate, setToDate] = useState(initialDates.lastDate);

  const {
    loading: dataLoading,
    error,
    data,
    refetch,
  } = useQuery(claimsGQL.CLAIMS_STATS_HISTORY, {
    variables: {
      input: {
        from_date: fromDate,
        to_date: toDate,
      },
    },
  });

  useEffect(() => {
    if (error?.graphQLErrors[0]?.message) {
      toast.error(error?.graphQLErrors[0]?.message);
    }
  }, [error]);

  const getLastNMonths = (n) => {
    const months = [];
    for (let i = 0; i < n; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1,
      );
      months.push(date);
    }
    return months;
  };

  const handleMonthClick = (date) => {
    const yearMonth = `${date.getFullYear()}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}`;
    const { firstDate, lastDate } = getFirstAndLastDateOfMonth(yearMonth);

    setFromDate(firstDate);
    setToDate(lastDate);

    refetch({
      input: {
        from_date: firstDate,
        to_date: lastDate,
      },
    });
  };

  const monthButtons = getLastNMonths(13).map((date, index) => {
    const isActive = convertToISOFormat(date.toLocaleDateString()) === fromDate;

    return (
      <AppButton
        key={index}
        variant={isActive ? "contained" : "outlined"}
        size="small"
        onClick={() => {
          handleMonthClick(date);
        }}
        label={`${date.getFullYear()} ${t(date.toLocaleString("default", { month: "long" }))}`}
        className={`mb-2 !text-[11px] !py-0.5 whitespace-nowrap ${
          isActive
            ? "!bg-[#0070C2] !text-white"
            : "!border-primary !text-primary hover:!bg-primary/10"
        }`}
      />
    );
  });

  return (
    <>
      <div className="pre_auth_container !p-0">
        <div className="flex justify-between flex-col lg:flex-row gap-2 mb-2">
          <div className="">
            <Tooltip
              placement="right"
              title={t("Keep track of your Claims History")}
              className="my-auto !mt-2"
            >
              <span className="font-semibold text-black/70 text-xl ">
                {t("CLAIMS HISTORY")}
                <span className="text-lg ml-1">
                  ({data?.claimsStats?.data?.length || 0}{" "}
                  <span className="text-xs font-bold italic">
                    {t("Entries")}
                  </span>
                  )
                </span>
              </span>
            </Tooltip>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-2 ">{monthButtons}</div>

        {data?.claimsStats?.data && data?.claimsStats?.data?.length > 0
          ? data?.claimsStats?.data?.map((stats) => {
              const monthDate = new Date(stats.month);
              const monthName = monthDate.toLocaleString("default", {
                month: "long",
              });
              const year = monthDate.getFullYear();

              return (
                <div key={stats.month} className="mt-2">
                  {/* <h3 className="py-1 font-bold text-sm border-y-2">
                  {t(monthName)}, {year}
                </h3> */}

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 my-2">
                    {stats.claims.map((claim, idx) => (
                      <div
                        key={`${claim.payer_id}-${claim.batch_id}-${idx}`}
                        className="bg-white border border-gray-300 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
                      >
                        {/* Card Header */}
                        <div className="bg-gray-100 px-2 py-2 border-b border-gray-300">
                          <Typography
                            className="!text-[12px] !font-bold !text-slate-700 !uppercase  "
                            title={claim?.payer_name}
                          >
                            {claim?.payer_name || "___"}
                          </Typography>
                        </div>

                        <div className="p-2 flex-1 flex flex-col gap-1.5">
                          {/* Summary Info */}
                          <div className="flex flex-col gap-1">
                            <div className="flex justify-between items-center text-[10px]">
                              <span className="font-bold text-slate-500 uppercase tracking-wider">
                                {t("TPA")}
                              </span>
                              <span
                                className="font-semibold text-slate-700 truncate max-w-[120px]"
                                title={claim?.tpa_name_en}
                              >
                                {claim?.tpa_name_en || "___"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center text-[10px]">
                              <span className="font-bold text-slate-500 uppercase tracking-wider">
                                {t("Batch ID")}
                              </span>
                              <span className="font-mono font-bold text-[#0070C2]">
                                {claim?.batch_id || "___"}
                              </span>
                            </div>
                          </div>

                          <Divider />

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 gap-1">
                            <div className="flex justify-between items-center bg-gray-50 p-1 rounded-md border border-gray-100">
                              <span className="text-[9px] font-bold text-gray-500 uppercase">
                                {t("Total")}
                              </span>
                              <span className="text-[12px] font-bold text-gray-700">
                                {claim?.total_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-green-50 p-1 rounded-md border border-green-100">
                              <span className="text-[9px] font-bold text-green-600 uppercase">
                                {t("Approved")}
                              </span>
                              <span className="text-[12px] font-bold text-green-600">
                                {claim?.approved_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-lime-50 p-1 rounded-md border border-lime-100">
                              <span className="text-[9px] font-bold text-lime-600 uppercase">
                                {t("Partial")}
                              </span>
                              <span className="text-[12px] font-bold text-lime-600">
                                {claim?.partial_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-cyan-50 p-1 rounded-md border border-cyan-100">
                              <span className="text-[9px] font-bold text-cyan-600 uppercase">
                                {t("Pending")}
                              </span>
                              <span className="text-[12px] font-bold text-cyan-600">
                                {claim?.pending_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-teal-50 p-1 rounded-md border border-teal-100">
                              <span className="text-[9px] font-bold text-teal-600 uppercase">
                                {t("Pended")}
                              </span>
                              <span className="text-[12px] font-bold text-teal-600">
                                {claim?.pended_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-blue-50 p-1 rounded-md border border-blue-100">
                              <span className="text-[9px] font-bold text-blue-600 uppercase">
                                {t("Ready")}
                              </span>
                              <span className="text-[12px] font-bold text-blue-600">
                                {claim?.ready_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-violet-50 p-1 rounded-md border border-violet-100">
                              <span className="text-[9px] font-bold text-violet-600 uppercase">
                                {t("Abnormal")}
                              </span>
                              <span className="text-[12px] font-bold text-violet-600">
                                {claim?.abnormal_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-orange-50 p-1 rounded-md border border-orange-100">
                              <span className="text-[9px] font-bold text-orange-600 uppercase">
                                {t("Cancelled")}
                              </span>
                              <span className="text-[12px] font-bold text-orange-600">
                                {claim?.cancelled_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-red-50 p-1 rounded-md border border-red-100">
                              <span className="text-[9px] font-bold text-red-600 uppercase">
                                {t("Errored")}
                              </span>
                              <span className="text-[12px] font-bold text-red-600">
                                {claim?.errored_claims}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-rose-50 p-1 rounded-md border border-rose-100">
                              <span className="text-[9px] font-bold text-rose-600 uppercase">
                                {t("Rejected")}
                              </span>
                              <span className="text-[12px] font-bold text-rose-600">
                                {claim?.rejected_claims}
                              </span>
                            </div>
                            <div className="col-span-2 flex justify-between items-center bg-amber-50 p-1 rounded-md border border-amber-100">
                              <span className="text-[9px] font-bold text-amber-600 uppercase">
                                {t("Validation Error")}
                              </span>
                              <span className="text-[12px] font-bold text-amber-600">
                                {claim?.validation_errors_claims}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          : !dataLoading && (
              <div className="py-4 flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-300 mt-4">
                <Typography className="!text-gray-400 !font-semibold !text-sm uppercase tracking-widest">
                  {t("No records found")}
                </Typography>

              </div>
            )}
      </div>

      <ClaimHistoryFilter
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        refetch={refetch}
      />

      <AppModal isOpen={dataLoading} />
    </>
  );
}

export default ClaimHistoryList;
